import { useEffect, useRef } from 'react';
import { AnimatePresence, LayoutGroup, m, useMotionValueEvent, useSpring } from 'framer-motion';
import { spring } from '../motion.js';
import { signed } from '../../../utils/mlb-the-show/format.js';

const rollVariants = {
  in: (d) => ({ y: d > 0 ? '70%' : '-70%', opacity: 0 }),
  show: { y: '0%', opacity: 1 },
  out: (d) => ({ y: d > 0 ? '-70%' : '70%', opacity: 0 }),
};

/** Value rolls up when it grows, down when it shrinks. */
export function Roll({ value, direction }) {
  const prev = useRef(value);
  const dir = direction ?? (Number(value) >= Number(prev.current) ? 1 : -1);
  useEffect(() => {
    prev.current = value;
  });
  return (
    <span className="st-roll">
      <AnimatePresence mode="popLayout" initial={false} custom={dir}>
        <m.span
          key={String(value)}
          custom={dir}
          variants={rollVariants}
          initial="in"
          animate="show"
          exit="out"
          transition={spring.standard}
        >
          {value}
        </m.span>
      </AnimatePresence>
    </span>
  );
}

const defaultFormat = (x) => Math.round(x).toLocaleString('en-US');

/** Counts to the new value by writing text directly, so React doesn't re-render per frame. */
export function Count({ value, format = defaultFormat }) {
  const node = useRef(null);
  const s = useSpring(value, { stiffness: 170, damping: 24 });
  useEffect(() => {
    s.set(value);
  }, [s, value]);
  useMotionValueEvent(s, 'change', (x) => {
    if (node.current) node.current.textContent = format(x);
  });
  return <span ref={node}>{format(s.get())}</span>;
}

export const formatMoney = (x) =>
  `$${Math.max(0, x).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** OVR badge with an optional rising +/− chip that replays when deltaKey changes. */
export function Ovr({ value, delta, deltaKey }) {
  return (
    <span className="st-ovr">
      OVR{' '}
      <b>
        <Roll value={value} />
      </b>
      {deltaKey && delta ? (
        <m.span
          key={deltaKey}
          className={`st-delta ${delta > 0 ? 'st-delta--up' : 'st-delta--down'}`}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: [0, 1, 1, 0], y: [4, -8, -10, -14] }}
          transition={{ duration: 2, times: [0, 0.12, 0.8, 1] }}
        >
          {signed(delta)}
        </m.span>
      ) : null}
    </span>
  );
}

export function Tabs({ id, label, options, value, onChange, small }) {
  return (
    <LayoutGroup id={id}>
      <div className={`st-tabs${small ? ' st-tabs--small' : ''}`} role="tablist" aria-label={label}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={value === option.value}
            className="st-tab"
            onClick={() => onChange(option.value)}
          >
            {value === option.value && <m.span layoutId="pill" className="st-tab-pill" transition={spring.snappy} />}
            <span>{option.label}</span>
            {option.count != null && <span className="st-tab-n">{option.count}</span>}
          </button>
        ))}
      </div>
    </LayoutGroup>
  );
}

export const SwapIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      d="M3 6h11l-3-3M15 12H4l3 3"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const DrawnCheck = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <m.path
      d="M3 8.5l3 3 7-7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.35, delay: 0.12 }}
    />
  </svg>
);

/** Reserved-height line; messages crossfade in place. */
export function Hint({ messageKey, children }) {
  return (
    <div className="st-hint" aria-live="polite">
      <AnimatePresence mode="popLayout" initial={false}>
        <m.span
          key={messageKey}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.18 }}
        >
          {children}
        </m.span>
      </AnimatePresence>
    </div>
  );
}
